import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useDeleteCampaign, useGetCampaigns } from "../../queries/campaign";
import { useGetActiveOrganiser } from "../../queries/user";
import { useMemo, useState, useEffect } from "react";
import Chip from "../../components/Chip";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from "../../components/TanStackTable";
import { FaEye, FaPencilAlt, FaTrash } from "react-icons/fa";
import { ActionButton } from '../../components/Forms/ActionButton';
import { IoIosFlash } from "react-icons/io";
import CampaignAPIModal from "../../components/CampaignAPIModal";
import CampaignDetailsModal from "../../components/CampaignDetailsModal";
const EditIcon = require("../../utils/media/icons/edit_icon.png");

const CampaignList = () => {

    const navigate = useNavigate();

    const { data, isPending } = useGetCampaigns();
    const { data: organiserList = [] } = useGetActiveOrganiser();
    const { mutateAsync: deleteFact, isPending: isDeleting } = useDeleteCampaign(data?._id);

    // Filter state
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        campaignName: '',
        campaignType: '',
        client: '',
        fromDate: '',
        toDate: '',
        status: ''
    });
    const [showAPIModal, setShowAPIModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCampaignForDetails, setSelectedCampaignForDetails] = useState(null);

    // Campaign type options
    const campaignTypeOptions = [
        { value: 'Sponsor a Challenge', label: 'Sponsor a Challenge' },
        { value: 'Sponsor a Blog', label: 'Sponsor a Blog' },
        { value: 'Promote Brand/Product', label: 'Promote Brand/Product' },
        { value: 'Plant Trees', label: 'Plant Trees' },
        { value: 'Promote an Event', label: 'Promote an Event' },
        { value: 'Advertisement', label: 'Advertisement' },
    ];

    // Status options
    const statusOptions = [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
    ];

    useEffect(() => {
        if (data) {
            setFilteredData(data);
        }
    }, [data]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = data;

        // Campaign name filter
        if (filters.campaignName) {
            filtered = filtered.filter(campaign => 
                campaign.title?.toLowerCase().includes(filters.campaignName.toLowerCase())
            );
        }

        // Campaign type filter
        if (filters.campaignType) {
            filtered = filtered.filter(campaign => 
                campaign.campaignType === filters.campaignType
            );
        }

        // Client filter
        if (filters.client) {
            filtered = filtered.filter(campaign => {
                const clientId = campaign.companyId?._id || campaign.NGOId?._id;
                return clientId === filters.client;
            });
        }

        // From date filter
        if (filters.fromDate) {
            filtered = filtered.filter(campaign => {
                if (!campaign.startDate) return false;
                const campaignDate = new Date(campaign.startDate);
                const filterDate = new Date(filters.fromDate);
                return campaignDate >= filterDate;
            });
        }

        // To date filter
        if (filters.toDate) {
            filtered = filtered.filter(campaign => {
                if (!campaign.endDate) return false;
                const campaignDate = new Date(campaign.endDate);
                const filterDate = new Date(filters.toDate);
                return campaignDate <= filterDate;
            });
        }

        // Status filter
        if (filters.status !== '') {
            const isActive = filters.status === 'true';
            filtered = filtered.filter(campaign => 
                campaign.active === isActive
            );
        }

        setFilteredData(filtered);
    };

    const handleViewAPIDetails = (campaign) => {
        setSelectedCampaign(campaign);
        setShowAPIModal(true);
    };

    const handleViewCampaignDetails = (campaign) => {
        setSelectedCampaignForDetails(campaign);
        setShowDetailsModal(true);
    };

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('title', {
            header: 'Campaign Name',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('campaignType', {
            header: 'Campaign Type',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor(row => row.companyId || row.NGOId, {
            id: 'client',
            header: 'Client',
            cell: info => {
                const campaign = info.row.original;
                const clientName = campaign.companyId?.companyName || campaign.NGOId?.ngoName || 'N/A';
                return (
                    <div className="whitespace-normal break-words">
                        {clientName}
                    </div>
                );
            },
        }),
        // columnHelper.accessor('challengeId', {
        //     header: 'Challenge',
        //     cell: info => (
        //         <div className="whitespace-normal break-words line-clamp-2">
        //             {info.getValue()?.name || 'N/A'}
        //         </div>
        //     ),
        // }),
        columnHelper.accessor('startDate', {
            header: 'Start Date',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                if (!date) return 'N/A';
                return (
                    <div className="whitespace-normal break-words text-center">
                        {date.toRegularDate()}
                        <p className="text-sm text-secondary">
                            {new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })}
                        </p>
                    </div>
                );
            },
        }),
        columnHelper.accessor('endDate', {
            header: 'End Date',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                if (!date) return 'N/A';
                return (
                    <div className="whitespace-normal break-words text-center">
                        {date.toRegularDate()}
                        <p className="text-sm text-secondary">
                            {new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })}
                        </p>
                    </div>
                );
            },
        }),
        columnHelper.accessor('active', {
            header: () => <div className="text-center">Status</div>,
            size: 10,
            align: 'center',
            cell: info => (
                <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />
            ),
        }),
        columnHelper.accessor('actions', {
            header: () => <div className="text-center">Actions</div>,
            enableColumnFilter: false,
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => navigate('/campaign-management/new-campaign', { state: { campaignId: info.row.original?._id } })}
                            title="Edit Campaign"
                        >
                            <FaPencilAlt className="fill-primary" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(255,193,7,0.1)] hover:bg-[rgba(255,193,7,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => handleViewAPIDetails(info.row.original)}
                            title="View API Details"
                        >
                            <IoIosFlash className="fill-orange" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[#29abe233] hover:bg-[#29abe244] mx-1 min-h-7 min-w-7"
                            onClick={() => handleViewCampaignDetails(info.row.original)}
                            title="View Campaign Details"
                        >
                            <FaEye className="fill-chipBlue" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            onClick={() => {
                                handleDelete(info.row.original._id);
                            }}
                            title="Delete Campaign"
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    </div>
                )
            },
        }),
    ], [navigate]);

    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this campaign?");
        if (!confirm) return;
        deleteFact(id, {
            onSettled: () => {
                toast.success('Campaign deleted successfully');
            },
            onError: (error) => {
                toast.error(`Error deleting campaign!`);
            },
        });
    }

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">List of Campaigns</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)]"
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full flex-wrap'>
                        {/* Campaign Name Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="campaignName" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Campaign Name
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="campaignName"
                                    value={filters.campaignName}
                                    onChange={handleFilterChange}
                                    placeholder="Search by campaign name..."
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                />
                            </div>
                        </div>

                        {/* Campaign Type Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="campaignType" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Campaign Type
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="campaignType"
                                    value={filters.campaignType}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Types</option>
                                    {campaignTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Client Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="client" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Client
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="client"
                                    value={filters.client}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Clients</option>
                                    {organiserList.map(organiser => (
                                        <option key={organiser._id} value={organiser._id}>
                                            {organiser.name || organiser.ngoName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full flex-wrap'>
                        {/* From Date Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                From Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="fromDate"
                                    value={filters.fromDate}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                />
                            </div>
                        </div>

                        {/* To Date Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                To Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="toDate"
                                    value={filters.toDate}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Status</option>
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                campaignName: '',
                                campaignType: '',
                                client: '',
                                fromDate: '',
                                toDate: '',
                                status: ''
                            });
                            setFilteredData(data);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full pl-6">
                    <h1 className="self-start text-lg font-bold pb-4">List of Campaigns</h1>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No campaigns available
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
            
            {/* API Details Modal */}
            <CampaignAPIModal
                isOpen={showAPIModal}
                onClose={() => {
                    setShowAPIModal(false);
                    setSelectedCampaign(null);
                }}
                campaign={selectedCampaign}
            />
            
            {/* Campaign Impact Details Modal */}
            <CampaignDetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedCampaignForDetails(null);
                }}
                campaign={selectedCampaignForDetails}
            />
        </main>
    );
}

export default CampaignList;